import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BottomNavbar from "../components/BottomNavbar";
import { ajukanJurnalMingguan, updateJurnalMingguan } from "../services/jurnalMingguanService";
import imageCompression from "browser-image-compression";
import { ApiConfig } from "../constants/apiConfig";
import logo from "../assets/EPRAJOGO1.png";

function InputJurnalMingguan() {
  const navigate = useNavigate();
  const location = useLocation();
  const journal = location.state?.journal;
  const siswaId = localStorage.getItem("user_id");

  const [mingguKe, setMingguKe] = useState(journal?.minggu_ke?.toString() || "");
  const [namaPekerjaan, setNamaPekerjaan] = useState(journal?.nama_pekerjaan || "");
  const [perencanaan, setPerencanaan] = useState(journal?.perencanaan_kegiatan || "");
  const [fotos, setFotos] = useState(() => (journal?.foto || []).map((photo) => ({
    id: photo.id,
    file: null,
    file_path: photo.file_path,
    deskripsi: photo.deskripsi || "",
    removed: false,
  })));
  const [loading, setLoading] = useState(false);

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };
    try {
      return await imageCompression(file, options);
    } catch (err) {
      console.error("Compression failed:", err);
      return file;
    }
  };

  const handleFotoChange = async (e) => {
    const files = Array.from(e.target.files);
    const activeCount = fotos.filter((foto) => !foto.removed).length;
    if (activeCount + files.length > 4) {
      alert("Maksimal upload 4 foto.");
      e.target.value = "";
      return;
    }

    try {
      const compressedFiles = await Promise.all(files.map((file) => compressImage(file)));
      const newFotoObjects = compressedFiles.map((file) => ({ file, deskripsi: "", removed: false }));
      setFotos((current) => [...current, ...newFotoObjects]);
    } catch (error) {
      alert(error.message || "Gagal memproses foto.");
    } finally {
      e.target.value = "";
    }
  };

  const updateDeskripsi = (index, value) => {
    setFotos((current) => current.map((foto, itemIndex) => itemIndex === index ? { ...foto, deskripsi: value } : foto));
  };

  const removeFoto = (index) => {
    setFotos((current) => current.flatMap((foto, itemIndex) => {
      if (itemIndex !== index) return [foto];
      return foto.id ? [{ ...foto, removed: true }] : [];
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const activePhotos = fotos.filter((foto) => !foto.removed);
    if (!mingguKe || !namaPekerjaan.trim() || !perencanaan.trim() || activePhotos.length === 0) {
      alert("Harap lengkapi semua isian.");
      return;
    }
    if (activePhotos.length > 4) {
      alert("Maksimal upload 4 foto.");
      return;
    }

    const formData = new FormData();
    formData.append("siswa_id", siswaId);
    formData.append("minggu_ke", mingguKe);
    formData.append("nama_pekerjaan", namaPekerjaan);
    formData.append("perencanaan_kegiatan", perencanaan);

    fotos.forEach((foto, index) => {
      if (foto.id) {
        formData.append(`foto[${index}][id]`, foto.id);
        if (foto.removed) {
          formData.append(`foto[${index}][delete]`, "true");
          return;
        }
      }
      if (foto.file) formData.append(`foto[${index}][file]`, foto.file);
      formData.append(`foto[${index}][deskripsi]`, foto.deskripsi || `Foto ${index + 1}`);
    });

    setLoading(true);
    const result = journal
      ? await updateJurnalMingguan(journal.id, formData)
      : await ajukanJurnalMingguan(formData);
    setLoading(false);

    if (result.success) {
      alert(result.message || (journal ? "Jurnal diperbaiki dan dikirim ulang untuk verifikasi." : "Jurnal berhasil dikirim."));
      navigate("/jurnal-mingguan");
    } else {
      alert(result.message || "Terjadi kesalahan saat menyimpan jurnal.");
    }
  };

  return (
    <main className="app-mobile-page">
      <header className="app-brand-header">
        <div className="app-brand-row">
          <img src={logo} alt="Logo E-Prajogo" className="app-brand-logo" />
          <div><p className="app-brand-name">E-PRAJOGO</p><p className="app-brand-school">SMK NEGERI GUDO</p></div>
          <span className="ms-auto rounded-pill px-3 py-2 small fw-semibold" style={{ background: "#ffffff20", color: "#fff" }}>SISWA</span>
        </div>
        <h1 className="app-header-title">{journal ? "Perbaiki Jurnal Mingguan" : "Input Jurnal Mingguan"}</h1>
        <p className="app-header-subtitle">Lengkapi rencana kegiatan dan dokumentasi pelaksanaan PKL.</p>
      </header>

      <section className="app-content">
      <form className="journal-form-card" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label fw-semibold">Pilih Minggu</label>
          <select
            className="form-select"
            value={mingguKe}
            onChange={(e) => setMingguKe(e.target.value)}
            required
          >
            <option value="">-- Pilih --</option>
            {[...Array(25)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                Minggu ke-{i + 1}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Nama Pekerjaan</label>
          <textarea
            className="form-control"
            placeholder="Contoh: Membuat laporan harian..."
            rows={2}
            value={namaPekerjaan}
            onChange={(e) => setNamaPekerjaan(e.target.value)}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Perencanaan Kegiatan</label>
          <textarea
            className="form-control"
            placeholder="Contoh: Menyusun rencana kegiatan minggu berikutnya..."
            rows={3}
            value={perencanaan}
            onChange={(e) => setPerencanaan(e.target.value)}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Upload Foto Pelaksanaan (maks 4)</label>
          <div className="d-flex flex-wrap gap-2">
            {fotos.map((foto, index) => foto.removed ? null : (
              <div key={index} style={{ width: 90 }}>
                <div className="position-relative" style={{ width: 90, height: 90 }}>
                  <img
                    src={foto.file ? URL.createObjectURL(foto.file) : (foto.file_path?.startsWith("http") ? foto.file_path : `${ApiConfig.baseImageUrl.replace(/\/$/, "")}/${(foto.file_path || "").replace(/^\//, "")}`)}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #ccc",
                    }}
                  />
                  <button
                    type="button"
                    className="btn-close position-absolute top-0 end-0"
                    style={{ backgroundColor: "white", fontSize: 10 }}
                    onClick={() => removeFoto(index)}
                  />
                </div>
                <input
                  type="text"
                  className="form-control mt-1"
                  placeholder={`Deskripsi foto ${index + 1}`}
                  value={foto.deskripsi}
                  onChange={(e) => updateDeskripsi(index, e.target.value)}
                />
              </div>
            ))}
            {fotos.filter((foto) => !foto.removed).length < 4 && (
              <label
                htmlFor="foto-upload"
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: 90,
                  height: 90,
                  border: "1px dashed #ccc",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-camera" style={{ fontSize: 24 }}></i>
              </label>
            )}
            <input
              id="foto-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFotoChange}
              style={{ display: "none" }}
            />
          </div>
        </div>

        <button
          className="btn btn-primary w-100 mt-4"
          type="submit"
          disabled={loading}
          style={{ borderRadius: 12 }}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Menyimpan...
            </>
          ) : (
            <>
              <i className="bi bi-save me-2" />
              {journal ? "Kirim Ulang untuk Verifikasi" : "Simpan"}
            </>
          )}
        </button>
      </form>
      </section>
      <BottomNavbar />
    </main>
  );
}

export default InputJurnalMingguan;
